<template>
  <section class="welfare-card vip-bonus-history" aria-label="晋级彩金记录" :aria-busy="loading">
    <header class="history-heading">
      <div class="history-title"><h2>晋级彩金记录</h2><span v-if="total > 0" class="history-count">{{ total }} 条</span></div>
      <button class="history-refresh" type="button" :disabled="loading" @click="load(true)">
        <svg :class="{ 'is-spinning': loading }" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5"/><path d="M6.1 7a7 7 0 0 1 11.6-1L20 9M4 15l2.3 3a7 7 0 0 0 11.6-1"/></svg>
        {{ loading ? '刷新中…' : '刷新' }}
      </button>
    </header>
    <div v-if="error" class="history-feedback history-feedback--error" role="alert">
      <ErrorNotice :message="error" />
      <p>请点击刷新重试</p>
    </div>
    <div v-else-if="loading && !records.length" class="history-empty" role="status"><span class="history-loader" aria-hidden="true"/><p>记录加载中…</p></div>
    <div v-else-if="!records.length" class="history-empty" role="status">
      <span class="history-empty-icon" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><path d="M10 5.5h10l4 4V27l-4-2-4 2-4-2-4 2V7.5a2 2 0 0 1 2-2Z"/><path d="M19.5 5.5v5h4M12 15h8M12 19h5"/></svg></span>
      <b>暂无晋级彩金记录</b>
      <p>申请后，可在这里查看彩金与处理进度</p>
    </div>
    <article v-for="record in records" :key="record.id" class="history-row">
      <div class="history-row-heading"><b>{{ record.title }}</b><span class="history-status" :data-status="record.status">{{ statusText[record.status] || '状态待核对' }}</span></div>
      <p class="history-amount">彩金金额 <strong>{{ record.amount }}</strong></p>
      <div class="history-times"><small>申请时间：{{ formatTime(record.appliedAt) }}</small><small v-if="record.completedAt">完成时间：{{ formatTime(record.completedAt) }}</small></div>
    </article>
    <button v-if="records.length < total" class="history-more" type="button" :disabled="loading" @click="load(false)">{{ loading ? '加载中…' : '加载更多' }}</button>
  </section>
</template>

<script setup>
import ErrorNotice from '../feedback/ErrorNotice.vue'
import { onBeforeUnmount, ref, watch } from 'vue'
import { webApi } from '../../services/webApi'
import { authStorage } from '../../services/http'

const props = defineProps({ refreshKey: { type: Number, default: 0 } })
const records = ref([])
const total = ref(0)
const loading = ref(false)
const error = ref('')
const statusText = { 0: '未派发', 1: '派发中', 2: '已派发', 3: '已取消', 4: '被拦截', 10: '被拦截' }
let pageNumber = 0
let sequence = 0
let disposed = false

function formatTime(value) { return value ? new Date(Number(value) * 1000).toLocaleString('zh-CN') : '—' }
async function load(reset = true) {
  if (!reset && loading.value) return
  const version = ++sequence
  const token = authStorage.getToken()
  const nextPage = reset ? 1 : pageNumber + 1
  loading.value = true
  error.value = ''
  if (reset) { records.value = []; total.value = 0; pageNumber = 0 }
  try {
    const result = await webApi.vipBonuses({ pageNumber: nextPage, pageSize: 10 })
    if (disposed || version !== sequence || token !== authStorage.getToken()) return
    if (!Array.isArray(result?.data)) throw new Error('记录返回格式错误')
    records.value = reset ? result.data : [...records.value, ...result.data]
    total.value = Number(result.total || 0)
    pageNumber = nextPage
  } catch (loadError) {
    if (!disposed && version === sequence && token === authStorage.getToken()) error.value = loadError.message || '记录查询失败，请重试'
  } finally { if (!disposed && version === sequence) loading.value = false }
}
watch(() => props.refreshKey, () => load(true), { immediate: true })
onBeforeUnmount(() => { disposed = true; sequence += 1 })
</script>

<style scoped>
.vip-bonus-history { padding: 24px; border: 1px solid var(--line, #dce8f5); border-radius: 18px; background: var(--panel, #fff); box-shadow: none; color: var(--ink, #26344b); }
.history-heading, .history-title, .history-row-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.history-heading { padding-bottom: 16px; border-bottom: 1px solid #edf2f8; }
.history-title { justify-content: flex-start; min-width: 0; }
.vip-bonus-history .history-heading h2 { margin: 0; font-size: 19px; line-height: 1.4; font-weight: 600; }
.history-count { flex-shrink: 0; padding: 2px 7px; border-radius: 5px; background: #f0f5fc; color: var(--muted, #6e7f98); font-size: 12px; font-variant-numeric: tabular-nums; }
.history-refresh, .history-more { display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 40px; padding: 0 13px; border: 1px solid #d8e9fa; border-radius: 9px; background: #f1f8ff; color: #1887d8; font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: background .18s, border-color .18s; }
.history-refresh { flex-shrink: 0; }
.history-refresh svg { width: 16px; height: 16px; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.history-refresh:hover:not(:disabled), .history-more:hover:not(:disabled) { background: #e6f3ff; border-color: #b9dbf9; }
.history-refresh:disabled, .history-more:disabled { cursor: default; opacity: .6; }
.history-refresh:focus-visible, .history-more:focus-visible { outline: 2px solid var(--blue, #179af8); outline-offset: 3px; }
.history-empty { display: flex; min-height: 168px; flex-direction: column; align-items: center; justify-content: center; padding: 24px 12px 18px; text-align: center; }
.history-empty-icon { display: grid; width: 48px; height: 48px; place-items: center; margin-bottom: 12px; border-radius: 14px; background: #f0f6fd; color: #82a8ce; }
.history-empty-icon svg { width: 30px; height: 30px; stroke: currentColor; stroke-width: 1.5; stroke-linecap: round; stroke-linejoin: round; }
.history-empty b { font-size: 15px; font-weight: 500; color: #536981; }
.vip-bonus-history .history-empty p { margin: 6px 0 0; color: #8594a8; font-size: 13px; line-height: 1.7; }
.history-feedback { display: flex; align-items: flex-start; gap: 10px; padding: 24px 0 8px; }
.history-feedback--error { color: #b34e52; }
.vip-bonus-history .history-feedback p { margin: 6px 0 0; color: var(--muted, #6e7f98); font-size: 13px; }
.history-row { padding: 20px 0; }
.history-row + .history-row { border-top: 1px solid #edf2f8; }
.history-row-heading > b { min-width: 0; font-size: 15px; line-height: 1.5; font-weight: 500; overflow-wrap: anywhere; }
.history-status { flex-shrink: 0; padding: 4px 9px; border-radius: 6px; color: #728296; background: #f1f4f8; font-size: 12px; line-height: 1.5; }
.history-status[data-status="1"] { color: #167cbf; background: #edf7ff; }
.history-status[data-status="2"] { color: #287b63; background: #edf8f3; }
.history-status[data-status="4"], .history-status[data-status="10"] { color: #b34e52; background: #fff0f1; }
.vip-bonus-history .history-amount { display: flex; align-items: baseline; gap: 12px; margin: 12px 0 10px; font-size: 13px; color: var(--muted, #6e7f98); }
.history-amount strong { color: #1686d3; font-size: 23px; line-height: 1.3; font-weight: 600; font-variant-numeric: tabular-nums; }
.history-times { display: flex; flex-wrap: wrap; gap: 6px 24px; }
.history-times small { color: #8594a8; font-size: 12px; line-height: 1.6; font-variant-numeric: tabular-nums; }
.history-more { display: flex; min-width: 116px; margin: 4px auto 0; }
.history-loader { width: 24px; height: 24px; margin-bottom: 8px; border: 2px solid #e5eef8; border-top-color: #55a5dd; border-radius: 50%; animation: history-spin .8s linear infinite; }
.is-spinning { animation: history-spin .8s linear infinite; }
@keyframes history-spin { to { transform: rotate(360deg); } }
@media (max-width: 640px) {
  .vip-bonus-history { padding: 18px; border-radius: 16px; }
  .vip-bonus-history .history-heading h2 { font-size: 17px; }
  .history-heading { gap: 8px; padding-bottom: 12px; }
  .history-title { gap: 6px; flex-wrap: wrap; }
  .history-refresh, .history-more { min-height: 44px; }
  .history-empty { min-height: 170px; padding-inline: 0; }
  .history-times { flex-direction: column; }
}
@media (prefers-reduced-motion: reduce) {
  .history-refresh, .history-more { transition: none; }
  .is-spinning, .history-loader { animation: none; }
}
</style>
